import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Button,
    Group,
} from '@mantine/core';
import {
    PlatformLoader,
    SimplePageContentWrapper,
    SimplePageLayout,
} from '@flexinet/ui-components';

import {useProviderConnectionQuery} from '../hooks/useProviderConnectionsQuery';
import {DetailPageNavigation} from '../navigation/DetailPageNavigation';
import {Profile} from '../components/Profile';
import config from '../config';

// FNP-1248 — the detail page.
//
// ⚠️ FOUR DISTINCT STATES, none collapsed into another:
//   loading   — the request is in flight; nothing is known yet.
//   error     — the request FAILED; still nothing is known.
//   not found — the request SUCCEEDED and said there is no such connection.
//   found     — render it.
//
// Error and not-found are different answers to different questions, and merging
// them sends a tenant looking for a connection they deleted, or hides a broken
// API behind a "not found" that suggests they typed something wrong.

export const DetailPage = () => {

    const {connectionId} = useParams<{ connectionId: string }>();
    const navigate = useNavigate();

    // ⚠️ DECODED. The id is composite and contains '#'
    // ('PROVIDER_CONNECTION#<accountId>#<uuid>'), so every link that builds one
    // encodes it — and this is the matching decode. Reading the raw param would
    // truncate the id at the first '#' in some routing configurations and look
    // up a connection that does not exist.
    const id = connectionId ? decodeURIComponent(connectionId) : undefined;

    const {
        data: connection,
        isLoading,
        isError,
        refetch,
    } = useProviderConnectionQuery(id);

    const backToIndex = () => navigate(config.basePath);

    if (isLoading) {
        return (
            <SimplePageLayout>
                <PlatformLoader message={I18n.get('Loading connection…')}/>
            </SimplePageLayout>
        );
    }

    // The request FAILED — nothing is known. Offers a retry, because the
    // connection may well be there.
    if (isError) {
        return (
            <SimplePageLayout>
                <SimplePageContentWrapper>
                    <Alert color={'red'} title={I18n.get('Could not load this connection')}>
                        <Group justify={'space-between'} align={'center'}>
                            {I18n.get('The domains API did not answer. This is not a provider problem — the request itself failed.')}
                            <Group gap={'xs'}>
                                <Button variant={'outline'} size={'xs'} onClick={() => refetch()}>
                                    {I18n.get('Retry')}
                                </Button>
                                <Button variant={'subtle'} size={'xs'} onClick={backToIndex}>
                                    {I18n.get('Back to connections')}
                                </Button>
                            </Group>
                        </Group>
                    </Alert>
                </SimplePageContentWrapper>
            </SimplePageLayout>
        );
    }

    // The request SUCCEEDED and said there is nothing here. No retry offered —
    // retrying a definitive answer just repeats it.
    if (!connection) {
        return (
            <SimplePageLayout>
                <SimplePageContentWrapper>
                    <Alert color={'yellow'} title={I18n.get('Connection not found')}>
                        <Group justify={'space-between'} align={'center'}>
                            {I18n.get('This provider connection does not exist. It may have been disconnected.')}
                            <Button variant={'outline'} size={'xs'} onClick={backToIndex}>
                                {I18n.get('Back to connections')}
                            </Button>
                        </Group>
                    </Alert>
                </SimplePageContentWrapper>
            </SimplePageLayout>
        );
    }

    return (
        <SimplePageLayout
            pageNavigation={<DetailPageNavigation component={connection}/>}
        >
            <SimplePageContentWrapper>
                {/* After a disconnect the record this page is built on no longer
                    exists, so the page leaves rather than rendering a stale one
                    or flashing its own not-found state. */}
                <Profile
                    connection={connection}
                    onDisconnected={backToIndex}
                />
            </SimplePageContentWrapper>
        </SimplePageLayout>
    );
};
